import Dexie, { type EntityTable } from "dexie";
import { motiveDocumentSchema, type MotiveDocument } from "../model/types";

export type ProjectRecord = {
  id: string;
  name: string;
  createdAt: string;
  modifiedAt: string;
  lastOpenedAt: string;
  description?: string;
  storage: "opfs" | "indexeddb";
  documentJson?: string;
};

class ModelMotiveDatabase extends Dexie {
  projects!: EntityTable<ProjectRecord, "id">;

  constructor() {
    super("modelmotive");
    this.version(1).stores({
      projects: "id, name, modifiedAt, lastOpenedAt"
    });
  }
}

export const db = new ModelMotiveDatabase();

async function opfsRoot(): Promise<FileSystemDirectoryHandle | null> {
  if (!("storage" in navigator) || typeof navigator.storage.getDirectory !== "function") return null;
  try {
    return await navigator.storage.getDirectory();
  } catch {
    return null;
  }
}

async function projectDirectory(projectId: string, create: boolean): Promise<FileSystemDirectoryHandle | null> {
  const root = await opfsRoot();
  if (!root) return null;
  try {
    const projects = await root.getDirectoryHandle("projects", { create });
    return await projects.getDirectoryHandle(projectId, { create });
  } catch {
    return null;
  }
}

async function writeOpfsDocument(document: MotiveDocument): Promise<boolean> {
  const directory = await projectDirectory(document.id, true);
  if (!directory) return false;
  const json = JSON.stringify(document);
  const temporary = await directory.getFileHandle("document.tmp.json", { create: true });
  const temporaryWritable = await temporary.createWritable();
  await temporaryWritable.write(json);
  await temporaryWritable.close();

  const verified = motiveDocumentSchema.parse(JSON.parse(await (await temporary.getFile()).text()));
  const primary = await directory.getFileHandle("document.json", { create: true });
  const primaryWritable = await primary.createWritable();
  await primaryWritable.write(JSON.stringify(verified));
  await primaryWritable.close();
  await directory.removeEntry("document.tmp.json").catch(() => undefined);
  return true;
}

async function readOpfsDocument(projectId: string): Promise<MotiveDocument | null> {
  const directory = await projectDirectory(projectId, false);
  if (!directory) return null;
  try {
    const handle = await directory.getFileHandle("document.json");
    return motiveDocumentSchema.parse(JSON.parse(await (await handle.getFile()).text()));
  } catch {
    return null;
  }
}

export async function saveProject(document: MotiveDocument): Promise<void> {
  const parsed = motiveDocumentSchema.parse(document);
  const savedToOpfs = await writeOpfsDocument(parsed);
  const now = new Date().toISOString();
  await db.projects.put({
    id: parsed.id,
    name: parsed.name,
    createdAt: parsed.metadata.createdAt,
    modifiedAt: parsed.metadata.modifiedAt,
    lastOpenedAt: now,
    description: parsed.metadata.description,
    storage: savedToOpfs ? "opfs" : "indexeddb",
    documentJson: savedToOpfs ? undefined : JSON.stringify(parsed)
  });
}

export async function loadProject(projectId: string): Promise<MotiveDocument> {
  const record = await db.projects.get(projectId);
  if (!record) throw new Error("Project not found");
  const opfs = record.storage === "opfs" ? await readOpfsDocument(projectId) : null;
  const document = opfs ?? (record.documentJson ? motiveDocumentSchema.parse(JSON.parse(record.documentJson)) : null);
  if (!document) throw new Error("The local project file could not be read");
  await db.projects.update(projectId, { lastOpenedAt: new Date().toISOString() });
  return document;
}

export async function listRecentProjects(limit = 12): Promise<ProjectRecord[]> {
  return db.projects.orderBy("lastOpenedAt").reverse().limit(limit).toArray();
}

export async function deleteProject(projectId: string): Promise<void> {
  await db.projects.delete(projectId);
  const root = await opfsRoot();
  if (!root) return;
  try {
    const projects = await root.getDirectoryHandle("projects");
    await projects.removeEntry(projectId, { recursive: true });
  } catch {
    // The IndexedDB record has already been removed.
  }
}

export async function requestPersistentStorage(): Promise<boolean> {
  if (!("storage" in navigator) || typeof navigator.storage.persist !== "function") return false;
  return navigator.storage.persist();
}

export async function storageEstimate(): Promise<{ usage: number; quota: number }> {
  if (!("storage" in navigator) || typeof navigator.storage.estimate !== "function") return { usage: 0, quota: 0 };
  const estimate = await navigator.storage.estimate();
  return { usage: estimate.usage ?? 0, quota: estimate.quota ?? 0 };
}
